import React, { useState, useEffect } from 'react';
import { supabase } from '../supabaseClient';
import { useNavigate } from 'react-router-dom';
import { AuthShell, authInputClass } from '../lib/authUi';

const EyeIcon = ({ show }) => (
  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    {show
      ? <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 4.411m0 0L21 21" />
      : <><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></>
    }
  </svg>
);

export default function ChangePassword() {
  const navigate = useNavigate();
  const [form, setForm] = useState({ password: '', confirm: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [ready, setReady] = useState(false);
  // Tracks whether the page was opened via a PASSWORD_RECOVERY email
  // link (signed-out user resetting a forgotten password) vs reached
  // by a signed-in user from their account menu. The success copy +
  // redirect framing differs: recovery users end up at login; signed-in
  // users bounce back to their portal/dashboard via App.js's defaultRoute.
  const [cameViaRecovery, setCameViaRecovery] = useState(false);

  useEffect(() => {
    supabase.auth.onAuthStateChange(async (event, session) => {
      if (event === 'PASSWORD_RECOVERY') {
        setReady(true);
        setCameViaRecovery(true);
      } else if (session) {
        setReady(true);
      }
    });
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session) setReady(true);
    });
  }, []);

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    if (!form.password || !form.confirm) { setError('Please fill in all fields'); return; }
    if (form.password.length < 8) { setError('Password must be at least 8 characters'); return; }
    if (form.password !== form.confirm) { setError('Passwords do not match'); return; }
    setSaving(true);
    setError(null);
    const { error } = await supabase.auth.updateUser({ password: form.password });
    if (error) { setError(error.message); setSaving(false); return; }
    setSuccess(true);
    setSaving(false);
    setTimeout(() => navigate('/'), 2000);
  };

  return (
    <AuthShell>
      {/* Card tokenised to the Fusion login standard: bg #18181A, 1px
          rgba(255,255,255,0.06) border, 16px radius, 430px max — matches
          Login/AuthShell so the whole auth family reads identically. */}
      <form onSubmit={handleSubmit} className="relative w-full max-w-[430px]">
        <div className="bg-[#18181A] border border-white/[0.06] rounded-[16px] shadow-2xl px-10 pt-10 pb-9">

          <div className="text-center mb-7">
            <p className="font-['Metropolis'] text-[13px] font-semibold tracking-[0.22em] uppercase text-[#92D400] mb-2">
              Fusion 2K
            </p>
            <h1 className="font-['Metropolis'] text-[30px] font-extrabold tracking-[0.04em] uppercase text-white leading-none">
              Change password
            </h1>
            <p className="font-['Metropolis'] text-[15px] text-[#9A9AA0] mt-4 leading-relaxed">
              Choose a new password to continue
            </p>
          </div>

          {success ? (
            <div className="text-center py-4">
              <p className="font-['Metropolis'] text-lg font-bold text-[#92D400] mb-2">Password updated</p>
              <p className="font-['Metropolis'] text-sm text-[#9A9AA0]">
                {cameViaRecovery
                  ? 'Redirecting you to login…'
                  : 'Redirecting you back…'}
              </p>
            </div>
          ) : !ready ? (
            <div className="text-center py-4">
              <p className="font-['Metropolis'] text-sm text-[#9A9AA0]">Verifying your reset link…</p>
            </div>
          ) : (
            <>
              <div className="space-y-4">
                <div>
                  <label htmlFor="new-password" className="block font-['Metropolis'] text-[11px] font-bold tracking-[0.12em] uppercase text-[#9A9AA0] mb-1.5">New password</label>
                  <div className="relative">
                    <input
                      id="new-password"
                      type={showPassword ? 'text' : 'password'}
                      value={form.password}
                      onChange={e => setForm(prev => ({ ...prev, password: e.target.value }))}
                      placeholder="Min 8 characters"
                      className={authInputClass + ' pr-12'}
                    />
                    <button
                      type="button"
                      onMouseDown={() => setShowPassword(true)}
                      onMouseUp={() => setShowPassword(false)}
                      onMouseLeave={() => setShowPassword(false)}
                      className="absolute right-4 top-1/2 -translate-y-1/2 text-[#51534A] hover:text-active-green transition-colors"
                    >
                      <EyeIcon show={showPassword} />
                    </button>
                  </div>
                </div>

                <div>
                  <label htmlFor="confirm-password" className="block font-['Metropolis'] text-[11px] font-bold tracking-[0.12em] uppercase text-[#9A9AA0] mb-1.5">Confirm new password</label>
                  <div className="relative">
                    <input
                      id="confirm-password"
                      type={showConfirm ? 'text' : 'password'}
                      value={form.confirm}
                      onChange={e => setForm(prev => ({ ...prev, confirm: e.target.value }))}
                      placeholder="Re-enter new password"
                      className={authInputClass + ' pr-12'}
                    />
                    <button
                      type="button"
                      onMouseDown={() => setShowConfirm(true)}
                      onMouseUp={() => setShowConfirm(false)}
                      onMouseLeave={() => setShowConfirm(false)}
                      className="absolute right-4 top-1/2 -translate-y-1/2 text-[#51534A] hover:text-active-green transition-colors"
                    >
                      <EyeIcon show={showConfirm} />
                    </button>
                  </div>
                </div>

                {error && (
                  <div className="bg-[#E0290A]/10 border border-[#E0290A]/30 px-4 py-3 rounded-xl">
                    <p className="font-['Metropolis'] text-sm text-[#E0290A]">{error}</p>
                  </div>
                )}

                <button
                  type="button"
                  onClick={() => navigate(-1)}
                  className="block ml-auto font-['Metropolis'] text-[13px] font-medium text-[#9A9AA0] hover:text-[#92D400] transition-colors"
                >
                  Cancel
                </button>
              </div>

              {/* Canonical filled button (guideline §7) — inside the card,
                  matching Login's dark green-text action. */}
              <button
                type="submit"
                disabled={saving}
                className="w-full h-[52px] mt-7 rounded-[10px] bg-[#232326] hover:bg-[#2C2C30] border border-white/[0.06] font-['Metropolis'] text-[15px] font-bold uppercase tracking-[0.14em] text-[#92D400] transition-colors disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus-visible:ring-2 focus-visible:ring-fusion-green"
              >
                {saving ? 'Updating…' : 'Update Password'}
              </button>
            </>
          )}
        </div>
      </form>
    </AuthShell>
  );
}
